/** De scankiosk: scans blijven ook tijdens de roosterweergave actief. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { haalRooster, haalStatus } from './api';
import { config } from './config';
import { useAutoUitlog } from './hooks/useAutoUitlog';
import { useKlok } from './hooks/useKlok';
import { useRooster } from './hooks/useRooster';
import { useScanner } from './hooks/useScanner';
import { bepaalToonDag, schoolNu } from './lib/bepaalToonDag';
import { Header } from './components/Header';
import { Legenda } from './components/Legenda';
import { LoginOverlay } from './components/LoginOverlay';
import { Roosterlijst } from './components/Roosterlijst';
import { UitlogBalk } from './components/UitlogBalk';
import { dagDatum, dagISO } from './lib/tijd';
import { ApiFout, type Rooster } from './types';

export function App() {
  const [leerlingnummer, setLeerlingnummer] = useState<string | null>(null);
  const [startRooster, setStartRooster] = useState<Rooster | null>(null);
  const [melding, setMelding] = useState('');
  const [bezig, setBezig] = useState(false);
  const [scanTeller, setScanTeller] = useState(0);
  const [dag, setDag] = useState(() => bepaalToonDag(new Date(), config.dagWisselTijd));
  const verzoek = useRef(0);
  const laatsteScan = useRef<{ nummer: string; tijd: number } | null>(null);
  const controller = useRef<AbortController | null>(null);
  const [testdatum, setTestdatum] = useState<string | null>(null);
  const datumVerzoek = useRef<Promise<string | null> | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    const refresh = () => {
      datumVerzoek.current = haalStatus(controller.signal).then((s) => {
        const datum = s.testdatum ?? null;
        setTestdatum(datum);
        return datum;
      }).catch(() => null);
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { controller.abort(); clearInterval(timer); };
  }, []);
  const { tijd: klokTijd } = useKlok(null);
  const tijd = schoolNu(testdatum, klokTijd);

  const uitloggen = useCallback((tekst = '') => {
    verzoek.current += 1;
    controller.current?.abort();
    setLeerlingnummer(null);
    setStartRooster(null);
    setBezig(false);
    setMelding(tekst);
  }, []);
  useEffect(() => () => {
    verzoek.current += 1;
    controller.current?.abort();
  }, []);
  useEffect(() => {
    if (!melding) return;
    const timer = setTimeout(() => setMelding(''), config.foutMeldingMs);
    return () => clearTimeout(timer);
  }, [melding, scanTeller]);

  const scan = useCallback(async (nummer: string) => {
    const nu = Date.now();
    if (laatsteScan.current?.nummer === nummer && nu - laatsteScan.current.tijd < config.scanHerhaalMs) return;
    laatsteScan.current = { nummer, tijd: nu };
    const id = ++verzoek.current;
    controller.current?.abort();
    const nieuwController = new AbortController();
    controller.current = nieuwController;
    setScanTeller((n) => n + 1);
    setLeerlingnummer(null);
    setStartRooster(null);
    setMelding('');
    setBezig(true);
    // Share the status poll, including an in-flight first load: no extra request
    // on every scan, and no date choice before the server date is known.
    const datum = datumVerzoek.current ? await datumVerzoek.current : testdatum;
    if (id !== verzoek.current) return;
    const toonDag = bepaalToonDag(schoolNu(datum), config.dagWisselTijd);
    setDag(toonDag);
    try {
      const rooster = await haalRooster(nummer, toonDag, nieuwController.signal);
      // Een vertraagd antwoord van een eerdere scan mag nooit opnieuw inloggen.
      if (id !== verzoek.current) return;
      setStartRooster(rooster);
      setLeerlingnummer(nummer);
    } catch (err) {
      if (id !== verzoek.current) return;
      setMelding(err instanceof ApiFout && err.nummerOngeldig
        ? 'Leerlingnummer niet herkend'
        : err instanceof Error ? err.message : 'Ophalen mislukt.');
    } finally {
      if (id === verzoek.current) setBezig(false);
    }
  }, [testdatum]);
  const buffer = useScanner((nummer) => { void scan(nummer); });
  useAutoUitlog(leerlingnummer !== null, config.autoUitlogMs, uitloggen, scanTeller);

  if (!leerlingnummer) return <LoginOverlay melding={melding} buffer={buffer} bezig={bezig} />;

  return <RoosterScherm key={scanTeller} leerlingnummer={leerlingnummer} dag={dag} tijd={tijd}
    startRooster={startRooster} uitloggen={uitloggen} scanTeller={scanTeller} />;
}

// Ontkoppelen bij iedere nieuwe scan wist de volledige vorige roosterstate en stopt polls.
function RoosterScherm({ leerlingnummer, dag, tijd, startRooster, uitloggen, scanTeller }: {
  leerlingnummer: string; dag: string; tijd: Date; startRooster: Rooster | null;
  uitloggen: (tekst?: string) => void; scanTeller: number;
}) {
  const { rooster } = useRooster(leerlingnummer, dag, uitloggen, startRooster);
  return (
    <div className="shell">
      <UitlogBalk key={scanTeller} duurMs={config.autoUitlogMs} />
      <Header leerlingnummer={leerlingnummer} leerling={rooster?.leerling ?? null} tijd={tijd} />
      <div className="panel">
        <div className="day-heading">
          <span className="day-tab actief">{dagDatum(dag)}</span>
          {rooster && (
            <span className="day-summary">
              {rooster.lessen.length} lesblokken · {rooster.lessen.filter((les) => les.status !== 'normal').length} wijzigingen
            </span>
          )}
        </div>
        <div className="toolbar">
          <Legenda />
        </div>
        <Roosterlijst rooster={rooster} tijd={tijd} toonNu={dag === dagISO(tijd)} />
      </div>
    </div>
  );
}
